import React, { useMemo, useState, useCallback } from "react";
import { Slider, InputNumber } from "antd";
import { MobileOutlined, TabletOutlined, LaptopOutlined, DesktopOutlined } from "@ant-design/icons";
import { typography, surfaces, borders, textColors, interactive } from "../../../design-system/tokens";
import InfoIcon from "../InfoIcon";
import ScreenSizeModeSelector from "./ScreenSizeModeSelector";

function clamp(n, min, max) {
  if (typeof n !== "number" || isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function deriveMobileDesktop(values, defaults) {
  const xs = typeof values?.extra_small === "number" ? values.extra_small : defaults[0];
  const s = typeof values?.small === "number" ? values.small : xs;
  const m = typeof values?.medium === "number" ? values.medium : defaults[2];
  const l = typeof values?.large === "number" ? values.large : m;
  const xl = typeof values?.extra_large === "number" ? values.extra_large : l;

  const isTwo = xs === s && m === l && l === xl;
  return { vMobile: xs, vDesktop: m, isTwo };
}

function SliderRow({ icon, label, value, onChange, min, max, step }) {
  // When there's no icon and no label (unified mode), start slider at beginning
  const hasLabelContent = icon || (label && label.length > 0);
  
  return (
    <div className="flex items-center gap-3 w-full">
      {hasLabelContent && (
        <div className={`w-24 flex items-center gap-2 ${textColors.primary} ${typography.bodySmall}`}>
          {icon && <span>{icon}</span>}
          <span className="font-medium">{label}</span>
        </div>
      )}
      <div className="flex-1">
        <Slider value={value ?? min} onChange={(v) => onChange(clamp(v, min, max))} min={min} max={max} step={step} />
      </div>
      <div className="ml-2 w-20">
        <InputNumber
          value={value}
          min={min}
          max={max}
          step={step}
          className="w-full"
          onChange={(num) => {
            const next = typeof num === "number" ? num : min;
            const bounded = clamp(next, min, max);
            onChange(bounded);
          }}
        />
      </div>
    </div>
  );
}

function SuggestionRow({ label, suggestions, selected, onPick }) {
  return (
    <div className="space-y-1.5">
      <div className={`${textColors.secondary} ${typography.caption}`}>{label}</div>
      <div className="flex gap-1.5 flex-wrap">
        {suggestions.map((px) => {
          const isSel = typeof selected === "number" && Number(selected) === Number(px);
          return (
            <button
              key={px}
              type="button"
              onClick={() => onPick(px)}
              className={`border rounded-lg px-2.5 py-1 transition-all ${typography.caption} font-medium ${
                isSel
                  ? `${interactive.selectedBg} text-white ${interactive.selectedBorder} shadow-sm`
                  : `${borders.default} ${surfaces.primary} ${textColors.primary} ${surfaces.hover}`
              }`}
              aria-pressed={isSel}
              title={`${px}px`}
            >
              {px}px
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ButtonFixedWidthField({ label, help, value, onChange, metadata, infoText }) {
  // Default button widths (reasonable starting points)
  const DEFAULTS = [200, 200, 200, 200, 200];
  const min = typeof metadata?.appearance_min === "number" ? metadata.appearance_min : 50;
  const max = typeof metadata?.appearance_max === "number" ? metadata.appearance_max : 800;
  const step = typeof metadata?.appearance_step_size === "number" ? metadata.appearance_step_size : 1;

  const { vMobile, vDesktop, isTwo } = useMemo(() => deriveMobileDesktop(value, DEFAULTS), [value]);
  const allEqual = useMemo(() => {
    const xs = value?.extra_small;
    return typeof xs === "number" && ["small", "medium", "large", "extra_large"].every((k) => value?.[k] === xs);
  }, [value]);
  const [mode, setMode] = useState(allEqual ? "one" : isTwo ? "two" : "all");

  const applyUnified = useCallback(
    (newVal) => {
      const v = clamp(newVal, min, max);
      onChange({ extra_small: v, small: v, medium: v, large: v, extra_large: v });
    },
    [onChange, min, max]
  );

  const applyMobile = useCallback(
    (newVal) => {
      const v = clamp(newVal, min, max);
      onChange({ extra_small: v, small: v });
    },
    [onChange, min, max]
  );

  const applyDesktop = useCallback(
    (newVal) => {
      const v = clamp(newVal, min, max);
      onChange({ medium: v, large: v, extra_large: v });
    },
    [onChange, min, max]
  );

  const collapseToTwo = useCallback(() => {
    const xs = typeof value?.extra_small === "number" ? value.extra_small : DEFAULTS[0];
    const m = typeof value?.medium === "number" ? value.medium : DEFAULTS[2];
    const vm = clamp(xs, min, max);
    const vd = clamp(m, min, max);
    onChange({ extra_small: vm, small: vm, medium: vd, large: vd, extra_large: vd });
    setMode("two");
  }, [onChange, value, min, max]);

  const unifyAll = useCallback(() => {
    const xs = typeof value?.extra_small === "number" ? value.extra_small : DEFAULTS[0];
    const v = clamp(xs, min, max);
    onChange({ extra_small: v, small: v, medium: v, large: v, extra_large: v });
    setMode("one");
  }, [onChange, value, min, max]);

  const expandToAll = useCallback(() => setMode("all"), []);

  const handleModeChange = useCallback((newMode) => {
    if (newMode === "one") unifyAll();
    else if (newMode === "two") collapseToTwo();
    else if (newMode === "all") expandToAll();
  }, [unifyAll, collapseToTwo, expandToAll]);

  // Button width presets (in pixels)
  const presets = [100, 150, 200, 250, 300, 500];

  return (
    <div className="field-wrapper">
      {label && (
        <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>
          {label}
          {infoText ? <span className="ml-2 align-middle"><InfoIcon text={infoText} /></span> : null}
        </label>
      )}
      {help && <div className={`mb-2 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}
      <ScreenSizeModeSelector
        mode={mode}
        onChange={handleModeChange}
        disabled={false}
      />
      <div className="space-y-3">

      {mode === "one" ? (
        <>
          <SliderRow icon={null} label="" value={value?.extra_small ?? DEFAULTS[0]} onChange={applyUnified} min={min} max={max} step={step} />
          <SuggestionRow
            label="Quick set"
            suggestions={presets}
            selected={typeof (value?.extra_small) === "number" ? value.extra_small : DEFAULTS[0]}
            onPick={(v) => applyUnified(v)}
          />
        </>
      ) : mode === "two" ? (
        <div className="space-y-3">
          <div className="space-y-2">
            <SliderRow icon={<MobileOutlined />} label="Phone" value={vMobile} onChange={applyMobile} min={min} max={max} step={step} />
            <SuggestionRow
              label="Phone presets"
              suggestions={presets}
              selected={vMobile}
              onPick={(v) => applyMobile(v)}
            />
          </div>
          <div className="space-y-2">
            <SliderRow icon={<DesktopOutlined />} label="Desktop" value={vDesktop} onChange={applyDesktop} min={min} max={max} step={step} />
            <SuggestionRow
              label="Desktop presets"
              suggestions={presets}
              selected={vDesktop}
              onPick={(v) => applyDesktop(v)}
            />
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-2">
            <SliderRow icon={<MobileOutlined />} label="Phone" value={value?.extra_small ?? DEFAULTS[0]} onChange={(v) => onChange({ extra_small: clamp(v, min, max) })} min={min} max={max} step={step} />
            <SliderRow icon={<TabletOutlined />} label="Tablet" value={value?.small ?? (value?.extra_small ?? DEFAULTS[1])} onChange={(v) => onChange({ small: clamp(v, min, max) })} min={min} max={max} step={step} />
            <SliderRow icon={<LaptopOutlined />} label="Laptop" value={value?.medium ?? DEFAULTS[2]} onChange={(v) => onChange({ medium: clamp(v, min, max) })} min={min} max={max} step={step} />
            <SliderRow icon={<DesktopOutlined />} label="Desktop" value={value?.large ?? (value?.medium ?? DEFAULTS[3])} onChange={(v) => onChange({ large: clamp(v, min, max) })} min={min} max={max} step={step} />
            <SliderRow icon={<DesktopOutlined />} label="Desk XL" value={value?.extra_large ?? (value?.large ?? DEFAULTS[4])} onChange={(v) => onChange({ extra_large: clamp(v, min, max) })} min={min} max={max} step={step} />
          </div>
          <SuggestionRow
            label="Quick set all"
            suggestions={presets}
            selected={(allEqual && typeof (value?.extra_small) === "number") ? value.extra_small : null}
            onPick={(v) => applyUnified(v)}
          />
        </>
      )}
      </div>
      <div className={`mt-2 ${typography.caption} ${textColors.tertiary}`}>
        Set fixed pixel widths for buttons. Button text will wrap if it exceeds the width.
      </div>
    </div>
  );
}

// Back-compat for tests and callsites that import a named export.
export { ButtonFixedWidthField };
